import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField } from '@mui/material'
import { VideoBlob, VideoBlobInsert, VideoBlobInsertFormatEnum } from '@nativewaves/nw-cloud-sdk/content'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
  SubmissionButton,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useVideoObjectBlobCreateMutation } from 'hooks/mutations/content'
import { videoObjectBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs'

type FormStructure = VideoBlobInsert

type NewDialogProps = {
  onSuccess?: (data: VideoBlob) => void
  initialValue?: VideoBlob
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const handleClose = useCloseHandler()

  const { videoobjectId } = videoObjectBlobsOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { mutate: createVideoBlob, ...videoBlobCreateProps } = useVideoObjectBlobCreateMutation()

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      ...initialValue,
      format: VideoBlobInsertFormatEnum.Hls,
      storeId: '1',
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Video.creatingBlob') })

      createVideoBlob(
        {
          videoId: videoobjectId,
          videoBlobInsert: data,
        },
        {
          onSuccess: (data) => {
            setTimeout(() => {
              handleClose()

              if (onSuccess) {
                setTimeout(() => {
                  onSuccess(data)
                }, 300)
              }
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: t('domain:Workspace.somethingWentWrong'),
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createVideoBlob, handleClose, onSuccess, videoobjectId],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Video.addBlob')}
      description={t('domain:Workspace.Video.blobCreateDescription')}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={videoBlobCreateProps.isSuccess} isError={videoBlobCreateProps.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!videoBlobCreateProps.isSuccess && !videoBlobCreateProps.isError}
          successCondition={videoBlobCreateProps.isSuccess}
          errorCondition={videoBlobCreateProps.isError}
          loading={videoBlobCreateProps.isPending}
          disabled={videoBlobCreateProps.isPending || videoBlobCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="storeId"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
